import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, PackageCheck, TruckIcon, CheckCircle2, ChevronDown, ChevronUp } from 'lucide-react';

const DAY_MS = 24 * 60 * 60 * 1000;

export const ActionAlerts = ({ loads }) => {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(null);

  const alerts = useMemo(() => {
    const now = new Date();
    const cutoff24h = new Date(now.getTime() + DAY_MS);

    const uncoveredSoon = [];
    const notReleased = [];
    const overdueDelivery = [];

    (loads || []).forEach((load) => {
      const stops = load.stops || [];
      const pickups = stops.filter((s) => s.type === 'pickup' && s.appointment_datetime);
      const deliveries = stops.filter((s) => s.type === 'delivery' && s.appointment_datetime);

      if (!load.carrier_id && ['Booked'].includes(load.status)) {
        const firstPickup = pickups.map((s) => new Date(s.appointment_datetime)).sort((a, b) => a - b)[0];
        if (firstPickup && firstPickup <= cutoff24h) {
          uncoveredSoon.push({ load, when: firstPickup });
        }
      }

      if (load.status === 'Delivered') {
        const lastDelivery = deliveries.map((s) => new Date(s.appointment_datetime)).sort((a, b) => b - a)[0];
        const days = lastDelivery ? Math.floor((now - lastDelivery) / DAY_MS) : null;
        if (days === null || days >= 2) notReleased.push({ load, days });
      }

      if (load.status === 'In Transit') {
        const lastDelivery = deliveries.map((s) => new Date(s.appointment_datetime)).sort((a, b) => b - a)[0];
        if (lastDelivery && lastDelivery < now) {
          overdueDelivery.push({ load, days: Math.floor((now - lastDelivery) / DAY_MS) });
        }
      }
    });

    return [
      {
        id: 'uncovered_soon',
        icon: AlertTriangle,
        color: 'var(--tms-error)',
        label: 'Uncovered, picking up within 24h',
        items: uncoveredSoon.map(({ load, when }) => ({
          load,
          detail: when.toLocaleString('en-US', { weekday: 'short', hour: 'numeric', minute: '2-digit' }),
        })),
      },
      {
        id: 'overdue_delivery',
        icon: TruckIcon,
        color: 'var(--tms-warning)',
        label: 'In Transit past delivery appointment',
        items: overdueDelivery.map(({ load, days }) => ({
          load,
          detail: days > 0 ? `${days} day${days === 1 ? '' : 's'} overdue` : 'overdue today',
        })),
      },
      {
        id: 'not_released',
        icon: PackageCheck,
        color: 'var(--tms-purple)',
        label: 'Delivered, not released 2+ days',
        items: notReleased.map(({ load, days }) => ({
          load,
          detail: days === null ? 'no delivery date' : `${days} day${days === 1 ? '' : 's'}`,
        })),
      },
    ].filter((g) => g.items.length > 0);
  }, [loads]);

  if (alerts.length === 0) {
    return (
      <div className="stats-card mb-4 sm:mb-6 lg:mb-8 flex items-center gap-2" style={{ padding: '0.875rem 1.5rem' }} data-testid="action-alerts-clear">
        <CheckCircle2 className="w-4 h-4 text-[var(--tms-success)]" />
        <span className="text-sm text-[var(--tms-text-secondary)]">No exceptions — all loads on track</span>
      </div>
    );
  }

  return (
    <div className="stats-card mb-4 sm:mb-6 lg:mb-8" style={{ padding: '1rem 1.5rem' }} data-testid="action-alerts-widget">
      <div className="flex flex-col gap-2">
        {alerts.map((group) => {
          const Icon = group.icon;
          const isOpen = expanded === group.id;
          return (
            <div key={group.id} data-testid={`alert-group-${group.id}`}>
              <button
                className="w-full flex items-center gap-2 text-left"
                onClick={() => setExpanded(isOpen ? null : group.id)}
                data-testid={`alert-toggle-${group.id}`}
              >
                <Icon className="w-4 h-4 flex-shrink-0" style={{ color: group.color }} />
                <span className="text-sm font-semibold text-[var(--tms-text)]">{group.items.length}</span>
                <span className="text-sm text-[var(--tms-text-secondary)] flex-1">{group.label}</span>
                {isOpen ? <ChevronUp className="w-4 h-4 text-[var(--tms-text-muted)]" /> : <ChevronDown className="w-4 h-4 text-[var(--tms-text-muted)]" />}
              </button>
              {isOpen && (
                <div className="mt-1 mb-1 pl-6 flex flex-wrap gap-2">
                  {group.items.slice(0, 12).map(({ load, detail }) => (
                    <button
                      key={load.id}
                      onClick={() => navigate(`/loads/${load.id}`)}
                      className="text-xs px-2 py-1 rounded border border-[var(--tms-border)] hover:border-[var(--tms-primary)] hover:text-[var(--tms-primary)] transition-colors"
                      data-testid={`alert-load-${load.load_number || load.id.substring(0, 8)}`}
                    >
                      <span className="font-mono font-bold">#{load.load_number || load.id.substring(0, 8)}</span>
                      <span className="text-[var(--tms-text-muted)]"> • {load.customer_name} • {detail}</span>
                    </button>
                  ))}
                  {group.items.length > 12 && (
                    <span className="text-xs text-[var(--tms-text-muted)] self-center">+{group.items.length - 12} more</span>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
